import { Component, OnInit } from '@angular/core';
import { Header } from '@aptsi-types';
import { finalize } from 'rxjs/operators';
import { SecureZoneDashboardService } from '../../shared/services/dashboard.service';

interface SecurityZoneDashboardResponse {
  summary?: {
    totalSecurityZones?: number;
    protectedAssets?: number;
    environments?: number;
    highRiskZones?: number;
  };
  zones?: any[];
  zoneTypeDistribution?: {
    labels?: string[];
    data?: number[];
  };
}

@Component({
  selector: 'app-security-zone-dashboard',
  templateUrl: './security-zone-dashboard.component.html',
  styleUrls: ['./security-zone-dashboard.component.scss']
})
export class SecurityZoneDashboardComponent implements OnInit {
  totalSecurityZones = 0;
  protectedAssets = 0;
  environments = 0;
  highRiskZones = 0;

  zoneDetails: any[] = [];

  zoneTypeDistribution: { labels: string[]; data: number[] } = {
    labels: [],
    data: []
  };

  isLoading = true;
  hasError = false;

  readonly headers: Header[] = [
    { name: 'Zone Name', property: 'zoneName', showInDropdown: true },
    { name: 'Environment', property: 'environment', showInDropdown: true },
    { name: 'Risk', property: 'risk', showInDropdown: true },
    { name: 'Assets', property: 'assets', showInDropdown: true },
    { name: 'Status', property: 'status', showInDropdown: true }
  ];

  chartData: any = {
    labels: [],
    datasets: []
  };

  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        align: 'start' as const,
        labels: {
          color: '#4b5870',
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 18
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: '#07112e',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 10,
        cornerRadius: 8,
        displayColors: true
      }
    }
  };

  constructor(
    private readonly secureZoneDashboardService: SecureZoneDashboardService
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  retryLoadDashboard(): void {
    this.loadDashboardData();
  }

  private loadDashboardData(): void {
    this.isLoading = true;
    this.hasError = false;

    this.secureZoneDashboardService
      .getSZDashboardData()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: SecurityZoneDashboardResponse) => {
          this.totalSecurityZones = response.summary?.totalSecurityZones ?? 0;
          this.protectedAssets = response.summary?.protectedAssets ?? 0;
          this.environments = response.summary?.environments ?? 0;
          this.highRiskZones = response.summary?.highRiskZones ?? 0;

          this.zoneDetails = response.zones ?? [];

          this.zoneTypeDistribution = {
            labels: response.zoneTypeDistribution?.labels ?? [],
            data: response.zoneTypeDistribution?.data ?? []
          };

          this.prepareChartData();
        },
        error: (error: unknown) => {
          this.hasError = true;
          console.error('Error loading security zone dashboard data:', error);
        }
      });
  }

  private prepareChartData(): void {
    this.chartData = {
      labels: this.zoneTypeDistribution.labels,
      datasets: [
        {
          label: 'Security Zones',
          data: this.zoneTypeDistribution.data,
          backgroundColor: [
            'rgba(40, 80, 173, 0.82)',
            'rgba(29, 127, 191, 0.82)',
            'rgba(25, 135, 84, 0.82)',
            'rgba(217, 140, 0, 0.82)',
            'rgba(220, 53, 69, 0.82)',
            'rgba(139, 92, 246, 0.82)',
            'rgba(13, 40, 99, 0.82)'
          ],
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 5
        }
      ]
    };
  }
}
